import { useLayoutEffect, useState, type RefObject } from 'react';

/** The smallest height that the hook gives. */
const FLOOR_PX = 160;

function scrollParent(el: HTMLElement): HTMLElement | null {
    for (let node = el.parentElement; node; node = node.parentElement) {
        const { overflowY } = getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
    }
    return null;
}

/** True when no visible element comes after `el` inside `stop`. */
function isLastContent(el: HTMLElement, stop: HTMLElement): boolean {
    for (let node: HTMLElement | null = el; node && node !== stop; node = node.parentElement) {
        for (let next = node.nextElementSibling; next; next = next.nextElementSibling) {
            if ((next as HTMLElement).offsetHeight > 0) return false;
        }
    }
    return true;
}

/** The bottom padding of each element from `el` up to `stop`. */
function paddingBelow(el: HTMLElement, stop: HTMLElement): number {
    let sum = 0;
    for (let node = el.parentElement; node && node !== stop; node = node.parentElement) {
        sum += parseFloat(getComputedStyle(node).paddingBottom) || 0;
    }
    return sum + (parseFloat(getComputedStyle(stop).paddingBottom) || 0);
}

/**
 * The height that lets `ref` fill its scroll region down to the bottom, or
 * undefined. It gives a height only when `enabled` is true and nothing on the
 * page comes below the element, so it never pushes other content down.
 */
export function useFillToBottom(
    ref: RefObject<HTMLElement | null>,
    enabled: boolean,
): number | undefined {
    const [height, setHeight] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!enabled || !el) {
            setHeight(undefined);
            return undefined;
        }
        const region = scrollParent(el);
        if (!region) return undefined;
        const measure = () => {
            if (!isLastContent(el, region)) {
                setHeight(undefined);
                return;
            }
            const space =
                region.getBoundingClientRect().bottom -
                el.getBoundingClientRect().top -
                paddingBelow(el, region);
            setHeight(Math.max(FLOOR_PX, Math.floor(space)));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(region);
        return () => ro.disconnect();
    }, [ref, enabled]);

    return height;
}
